import React, { useCallback, useEffect, useState } from 'react'
import { getSavedDpi, printZpl } from '../../utils/labelPrinter'
import { markLabelPrinted, pendingLabelJobs } from '../../utils/labelQueue'
import { useToast } from '../../hooks/useToast'

const API_BASE = import.meta.env.VITE_API_URL || ''

// The floor asks in bursts and nobody stands watching this panel, so a slow
// poll is enough; the operator who queued it has walked away regardless.
const POLL_MS = 30_000

/**
 * Reprint requests raised from the warehouse handheld.
 *
 * The handheld cannot print — Zebra Browser Print is a localhost agent with no
 * Android build — so this machine, which has the agent, does it on their
 * behalf. Nothing new is invented for the transport: the ZPL comes from the
 * same /api/labels/product/:id.zpl endpoint every other print button uses.
 *
 * A job is only marked printed after the printer has accepted it, so a failed
 * send leaves the request outstanding rather than quietly losing it.
 */
export default function LabelReprintQueue() {
    const [jobs, setJobs] = useState([])
    const [printingId, setPrintingId] = useState(null)
    const { toast } = useToast()

    const refresh = useCallback(async () => {
        try {
            setJobs(await pendingLabelJobs())
        } catch {
            // A settings panel that cannot reach the queue should stay quiet
            // rather than toast every 30 seconds.
        }
    }, [])

    useEffect(() => {
        refresh()
        const timer = setInterval(refresh, POLL_MS)
        return () => clearInterval(timer)
    }, [refresh])

    async function print(job) {
        setPrintingId(job.id)
        try {
            const response = await fetch(
                `${API_BASE}/api/labels/product/${job.shopify_product_id}.zpl?dpi=${getSavedDpi()}`,
                { credentials: 'include' }
            )
            if (!response.ok) throw new Error(`Could not build the label (HTTP ${response.status})`)
            await printZpl(await response.text())
            await markLabelPrinted(job.id)
            toast.success('Label printed')
            refresh()
        } catch (e) {
            // Deliberately left outstanding: the floor is still waiting.
            toast.error(e.message || 'Printing failed — the request stays in the queue')
        } finally {
            setPrintingId(null)
        }
    }

    return (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
            <h3 style={{ marginBottom: 'var(--space-3)' }}>
                Label reprints from the warehouse{jobs.length > 0 ? ` (${jobs.length})` : ''}
            </h3>

            {jobs.length === 0 ? (
                <p className="text-sm text-muted">
                    Nothing outstanding. Requests raised on the handheld appear here.
                </p>
            ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {jobs.map((job) => (
                        <li
                            key={job.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                padding: 'var(--space-2) 0',
                                borderBottom: '1px solid var(--color-gray-200)'
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div>{job.title || `Product ${job.shopify_product_id}`}</div>
                                <div className="text-sm text-muted">
                                    {job.item_id || job.shopify_product_id}
                                    {job.requested_by_name ? ` · asked by ${job.requested_by_name}` : ''}
                                </div>
                            </div>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => print(job)}
                                disabled={printingId !== null}
                            >
                                {printingId === job.id ? 'Printing…' : 'Print'}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
